import * as preact from "preact";

// Matches the same constructs as `simpleMarkupToHtml` in shared/utils.js:
// - **bold** → <strong>
// - https?://… → <a href="…">…</a> (trailing punctuation kept outside the link)
// - \n → <br>
//
// Unlike the HTML version, this returns Preact nodes that don't need to be
// fed through `dangerouslySetInnerHTML`.

const BOLD_REGEXP = /\*\*([^*]+)\*\*/g;
const URL_REGEXP = /https?:\/\/\S*/g;
const TRAILING_PUNCTUATION_REGEXP = /[.,<>(){}\-;:'"[\]]/;

const renderNewlines = (
  text: string,
  keyPrefix: string
): preact.ComponentChildren => {
  if (text === "") return null;
  const parts = text.split("\n");
  const result: preact.ComponentChildren[] = [];
  for (let index = 0; index < parts.length; index++) {
    if (index > 0) {
      result.push(<br key={`${keyPrefix}-br-${index}`} />);
    }
    if (parts[index] !== "") {
      result.push(parts[index]);
    }
  }
  return result;
};

const renderLinks = (
  text: string,
  keyPrefix: string
): preact.ComponentChildren => {
  const result: preact.ComponentChildren[] = [];
  let lastIndex = 0;
  let linkCount = 0;

  URL_REGEXP.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = URL_REGEXP.exec(text)) !== null) {
    let url = match[0];
    let trailing = "";
    while (
      url.length > 0 &&
      TRAILING_PUNCTUATION_REGEXP.test(url[url.length - 1])
    ) {
      trailing = url[url.length - 1] + trailing;
      url = url.substring(0, url.length - 1);
    }

    const before = text.substring(lastIndex, match.index);
    if (before)
      result.push(renderNewlines(before, `${keyPrefix}-pre-${linkCount}`));

    result.push(
      <a key={`${keyPrefix}-a-${linkCount}`} href={url}>
        {url}
      </a>
    );
    if (trailing) {
      result.push(renderNewlines(trailing, `${keyPrefix}-trail-${linkCount}`));
    }

    lastIndex = match.index + match[0].length;
    linkCount++;
  }

  const remainder = text.substring(lastIndex);
  if (remainder) {
    result.push(renderNewlines(remainder, `${keyPrefix}-rest`));
  }
  return result;
};

export const simpleMarkupToJsx = (
  input: string | null | undefined
): preact.ComponentChildren => {
  if (!input) return null;

  const result: preact.ComponentChildren[] = [];
  let lastIndex = 0;
  let boldCount = 0;

  BOLD_REGEXP.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = BOLD_REGEXP.exec(input)) !== null) {
    const before = input.substring(lastIndex, match.index);
    if (before) result.push(renderLinks(before, `pre-${boldCount}`));

    result.push(
      <strong key={`bold-${boldCount}`}>
        {renderLinks(match[1], `bold-${boldCount}`)}
      </strong>
    );

    lastIndex = match.index + match[0].length;
    boldCount++;
  }

  const remainder = input.substring(lastIndex);
  if (remainder) result.push(renderLinks(remainder, "rest"));

  return result;
};
